"use client";

import { useEffect, useState } from "react";
import { Loader2, Pencil } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import type { CommandProject } from "@/types/command-agent";

interface RenameCommandProjectDialogProps {
  project: CommandProject | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRename: (projectId: string, name: string) => void;
  isPending?: boolean;
}

export function RenameCommandProjectDialog({
  project,
  open,
  onOpenChange,
  onRename,
  isPending,
}: RenameCommandProjectDialogProps) {
  const [name, setName] = useState("");

  useEffect(() => {
    if (project) setName(project.name);
  }, [project]);

  const submit = () => {
    const trimmed = name.trim();
    if (!project || trimmed.length < 2 || isPending) return;
    onRename(project.id, trimmed);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[9px] bg-violet-100 dark:bg-violet-900/30">
              <Pencil className="size-4 text-primary" />
            </div>
            <div className="space-y-1">
              <DialogTitle>Rename project</DialogTitle>
              <p className="text-sm text-muted-foreground">
                Chats inside keep their titles — only the folder changes.
              </p>
            </div>
          </div>
        </DialogHeader>
        <Input
          value={name}
          onChange={(event) => setName(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") submit();
          }}
          placeholder="Project name"
          maxLength={100}
          disabled={isPending}
          autoFocus
        />
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
            type="button"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={name.trim().length < 2 || isPending}
            type="button"
          >
            {isPending ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              "Save"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
